import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Eye, Loader2, Database, AlertCircle } from "lucide-react";
import { ScrollArea } from "./ui/scroll-area";
import api from "../services/api";

export function DataPreviewDialog({ 
  type, // "oracle" | "bronze" | "silver" | "gold"
  connectionId,
  schema, // for Oracle
  tableName,
  bigqueryConnectionId, // for BigQuery layers
  disabled,
  triggerButton // optional custom trigger
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const fetchPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      if (type === "oracle") {
        data = await api.previewOracleData(connectionId, tableName, 10, schema);
        // Transform Oracle response format
        setPreviewData({
          columns: data.data.length > 0 ? Object.keys(data.data[0]).map(k => ({ name: k, type: "VARCHAR2" })) : [],
          rows: data.data,
          total_rows: data.count,
          preview_rows: data.count
        });
      } else {
        // BigQuery layers (bronze, silver, gold)
        const layer = type === "bronze" ? "bronze_layer" : type === "silver" ? "silver_layer" : "gold_layer";
        data = await api.previewBigQueryData(bigqueryConnectionId, layer, tableName, 10);
        setPreviewData(data);
      }
    } catch (err) {
      console.error("Preview error:", err);
      setError(err.response?.data?.detail || err.message || "Failed to load preview");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (isOpen && !previewData) {
      fetchPreview();
    }
  };

  const getLayerColor = () => {
    switch (type) {
      case "oracle": return "bg-blue-500";
      case "bronze": return "bg-orange-500";
      case "silver": return "bg-slate-500";
      case "gold": return "bg-amber-500";
      default: return "bg-gray-500";
    }
  };

  const getLayerLabel = () => {
    switch (type) {
      case "oracle": return "Oracle Source";
      case "bronze": return "Bronze Layer";
      case "silver": return "Silver Layer";
      case "gold": return "Gold Layer";
      default: return type;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {triggerButton || (
          <Button 
            variant="outline" 
            size="sm" 
            disabled={disabled || !tableName}
            data-testid={`preview-${type}-${tableName}`}
          >
            <Eye className="w-4 h-4 mr-1" />
            Preview
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Data Preview
            <Badge className={`${getLayerColor()} text-white ml-2`}>
              {getLayerLabel()}
            </Badge>
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {type === "oracle" ? `${schema}.${tableName}` : `${type}_layer.${tableName}`}
          </p>
        </DialogHeader>

        <div className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading preview...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-destructive">
              <AlertCircle className="w-12 h-12 mb-2" />
              <p className="font-medium">Error loading preview</p>
              <p className="text-sm text-muted-foreground mt-1">{error}</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={fetchPreview}>
                Try Again
              </Button>
            </div>
          ) : previewData ? (
            <div className="space-y-4">
              {/* Stats */}
              <div className="flex gap-4 text-sm">
                <Badge variant="outline">
                  {previewData.total_columns || previewData.columns?.length || 0} columns
                </Badge>
                <Badge variant="outline">
                  {previewData.total_rows?.toLocaleString() || previewData.preview_rows || 0} total rows
                </Badge>
                <Badge variant="secondary">
                  Showing {previewData.preview_rows || previewData.rows?.length || 0} rows
                </Badge>
              </div>

              {/* Data Table */}
              <ScrollArea className="h-[400px] border rounded-lg">
                <Table>
                  <TableHeader className="sticky top-0 bg-muted">
                    <TableRow>
                      {previewData.columns?.map((col, idx) => (
                        <TableHead key={idx} className="font-mono text-xs whitespace-nowrap">
                          <div>{col.name}</div>
                          <div className="text-[10px] text-muted-foreground font-normal">{col.type}</div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.rows?.length > 0 ? (
                      previewData.rows.map((row, rowIdx) => (
                        <TableRow key={rowIdx}>
                          {previewData.columns?.map((col, colIdx) => (
                            <TableCell key={colIdx} className="font-mono text-xs max-w-[200px] truncate">
                              {row[col.name] === null ? (
                                <span className="text-muted-foreground italic">null</span>
                              ) : (
                                String(row[col.name])
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={previewData.columns?.length || 1} className="text-center py-8 text-muted-foreground">
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>

              {/* Refresh button */}
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={fetchPreview}>
                  Refresh Preview
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DataPreviewDialog;
